/* global debounce */

const RECENT_SEARCHES_KEY = 'kiko-recent-searches';
const MAX_RECENT_SEARCHES = 10;

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.resetBtn = this.querySelector('.js-search-reset');
      // Look for results in parent container if not found in predictive-search element
      this.results = this.querySelector('.js-search-results') ||
        document.querySelector('.header__search-content .js-search-results') ||
        document.querySelector('.search-dialog_searchWrapper___kGSn .js-search-results') ||
        document.querySelector('.dialog_content__u_95A .js-search-results');
      this.statusEl = this.querySelector('.js-search-status') ||
        document.querySelector('.header__search-content .js-search-status') ||
        document.querySelector('.search-dialog_searchWrapper___kGSn .js-search-status') ||
        document.querySelector('.dialog_content__u_95A .js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      if (!this.input) return;
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      this.input.addEventListener('keydown', this.handleInputKeydown.bind(this));
      const form = this.querySelector('.search') || this.input.closest('form');
      if (form) form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      const query = this.getQuery();
      if (query.length) {
        PredictiveSearch.saveRecentSearch(query);
      }
      if (!query.length || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        // Clear cached search results when input is cleared
        this.removeAttribute('results');
        // Show featured products when search is empty
        this.getFeaturedProducts();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) {
        // Show featured products when search is empty and focused
        this.getFeaturedProducts();
        return;
      }

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' on the search input (always active so Enter works even when results aren't open).
     * @param {object} evt - Event object.
     */
    handleInputKeydown(evt) {
      if (evt.key !== 'Enter') return;
      const selectedLink = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedLink) {
        evt.preventDefault();
        selectedLink.click();
        return;
      }
      const form = this.querySelector('.search') || (this.input && this.input.closest('form'));
      if (form && this.getQuery().length) {
        evt.preventDefault();
        form.submit();
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;
      // Enter on the input is already handled by handleInputKeydown to avoid double submit.
      if (evt.target === this.input && evt.key === 'Enter') return;

      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          evt.preventDefault();
          const selectedLink = this.querySelector('[aria-selected="true"] > .js-search-link');
          if (selectedLink) {
            selectedLink.click();
          } else {
            // No suggestion selected — submit form to go to search results page (e.g. /search?options[prefix]=last&q=eyes)
            const form = this.querySelector('.search') || (this.input && this.input.closest('form'));
            if (form && this.getQuery().length) {
              form.submit();
            }
          }
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector('[role="tabpanel"]:not([hidden]) [aria-selected="true"]');
      const allResults = this.querySelectorAll('[role="tabpanel"]:not([hidden]) .predictive-search__item');
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets featured products when search is empty.
     */
    /**
     * Preloads featured products without rendering them (for faster loading)
     */
    async preloadFeaturedProducts() {
      // If already cached or already loading, skip
      if (this.cachedFeaturedProducts || this._loadingFeaturedProducts) {
        return;
      }

      this._loadingFeaturedProducts = true;
      try {
        // Fetch the section using the sections route
        const url = new URL(window.location.href);
        url.searchParams.set('sections', 'featured-products-search');
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(response.status);

        const data = await response.json();
        const sectionHTML = data['featured-products-search'];

        if (sectionHTML) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = sectionHTML;

          const resultsEl = tmpl.content.querySelector('#shopify-section-featured-products-search');
          if (resultsEl) {
            const resultsMarkup = resultsEl.innerHTML;
            this.cachedFeaturedProducts = resultsMarkup;
          }
        }
      } catch (error) {
        // Silently fail if featured products can't be loaded
        console.error('Failed to preload featured products:', error);
      } finally {
        this._loadingFeaturedProducts = false;
      }
    }

    async getFeaturedProducts() {
      // Check if we have cached featured products
      if (this.cachedFeaturedProducts) {
        this.renderResults(this.cachedFeaturedProducts);
        const searchContent = document.querySelector('.header__search-content');
        if (searchContent) searchContent.removeAttribute('hidden');
        return;
      }

      // If already loading, wait a bit and try again
      if (this._loadingFeaturedProducts) {
        setTimeout(() => this.getFeaturedProducts(), 100);
        return;
      }

      this._loadingFeaturedProducts = true;
      try {
        // Fetch the section using the sections route
        const url = new URL(window.location.href);
        url.searchParams.set('sections', 'featured-products-search');
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(response.status);

        const data = await response.json();
        const sectionHTML = data['featured-products-search'];

        if (sectionHTML) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = sectionHTML;

          const resultsEl = tmpl.content.querySelector('#shopify-section-featured-products-search');
          if (resultsEl) {
            const resultsMarkup = resultsEl.innerHTML;
            this.cachedFeaturedProducts = resultsMarkup;
            this.renderResults(resultsMarkup);
            const searchContent = document.querySelector('.header__search-content');
            if (searchContent) searchContent.removeAttribute('hidden');
          }
        }
      } catch (error) {
        // Silently fail if featured products can't be loaded
        console.error('Failed to load featured products:', error);
      } finally {
        this._loadingFeaturedProducts = false;
      }
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      const queryKey = searchTerm.replace(' ', '-').toLowerCase();
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = `q=${encodeURIComponent(searchTerm)}`;
      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${theme.settings.pSearchLimitScope}`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        let resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        // Extract queries from predictive search and inject suggestions
        const suggestionsMarkup = this.extractAndFormatSuggestions(resultsEl, searchTerm);
        if (suggestionsMarkup) {
          // Inject suggestions at the beginning of the results container
          const containerMatch = resultsMarkup.match(/<div class="predictive-search__results[^"]*">/);
          if (containerMatch) {
            const insertIndex = containerMatch.index + containerMatch[0].length;
            resultsMarkup = resultsMarkup.slice(0, insertIndex) + '<div class="w-full pt-6 pb-8">' + suggestionsMarkup + resultsMarkup.slice(insertIndex);
          }
        }

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }

      // Get results count
      let searchResultsParams = `q=${encodeURIComponent(searchTerm)}`;
      searchResultsParams += `&${encodeURIComponent('options[fields]')}=${searchFields}`;
      searchResultsParams += '&section_id=search-results-link';

      const srResponse = await fetch(`${theme.routes.search}?${searchResultsParams}`);
      if (!srResponse.ok) throw new Error(srResponse.status);

      const stTmpl = document.createElement('template');
      stTmpl.innerHTML = await srResponse.text();
      const viewAll = this.querySelector('.predictive-search__view-all');
      if (viewAll) {
      viewAll.innerText = stTmpl.content.querySelector('#shopify-section-search-results-link').innerText;
      viewAll.hidden = false;
      }
    }

    /**
     * Extracts queries from predictive search results and formats them as suggestions.
     * @param {Element} resultsEl - The predictive search results element.
     * @param {string} searchTerm - The current search term.
     * @returns {string} - HTML markup for suggestions or empty string.
     */
    extractAndFormatSuggestions(resultsEl, searchTerm) {
      const suggestionsEl = resultsEl.querySelector('.predictive-search__suggestions');
      if (!suggestionsEl) return '';

      const queriesList = suggestionsEl.querySelector('.predictive-result__list-suggestions');
      if (!queriesList || queriesList.children.length === 0) return '';

      const suggestionsHeading = this.getAttribute('data-suggestions-heading') || 'KIKO Suggestions';
      const fillSuggestionTemplate = this.getAttribute('data-fill-suggestion-template') || 'Fill in the search field with "__SUGGESTION__"';
      let suggestionsHTML = `
        <div class="predictive-search__suggestions-block mb-8">
          <h2 class="predictive-result__title main-nav__heading mb-4 flex items-center gap-2">
            <span class="icon_iconContainer__lS_O1">
              <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 16" aria-hidden="true">
                <path d="M10.799 12.085H7.045c0-1.52-3.058-2.37-3.058-5.765 0-2.587 2.399-4.92 4.92-4.92 2.52 0 4.92 2.002 4.92 4.92 0 3.386-3.028 4.247-3.028 5.765ZM11.188 14.6H6.625M8.905 12.53V8.047M10.332 14.6H7.48M10.652 8.047H7.16" stroke="currentColor" stroke-width="2"></path>
              </svg>
            </span>
            <span>${suggestionsHeading}</span>
          </h2>
          <ul class="predictive-search__list predictive-result__list-suggestions flex flex-wrap gap-2 mb-4" role="list" aria-label="${suggestionsHeading}">
      `;

      // Extract up to 5 suggestions
      const suggestionItems = Array.from(queriesList.querySelectorAll('li')).slice(0, 5);
      suggestionItems.forEach(item => {
        const link = item.querySelector('a');
        if (link) {
          const styledText = link.innerHTML; // This contains the <mark> tags for highlighting
          const href = link.getAttribute('href');
          // Extract the actual search term from the URL
          const urlParams = new URLSearchParams(href.split('?')[1] || '');
          const suggestionTerm = urlParams.get('q') || link.textContent.trim();

          suggestionsHTML += `
            <li>
              <div class="predictive-search__suggestion-item">
                <p class="predictive-search__suggestion-text">${styledText}</p>
                <button type="button" 
                        class="predictive-search__suggestion-btn js-search-suggestion focus-inset" 
                        data-suggestion="${this.escapeHtml(suggestionTerm)}"
                        title="${this.escapeHtml(fillSuggestionTemplate.replace('__SUGGESTION__', suggestionTerm))}"
                        aria-label="${this.escapeHtml(fillSuggestionTemplate.replace('__SUGGESTION__', suggestionTerm))}">
                  <span class="icon_iconContainer__lS_O1">
                    <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 16" aria-hidden="true">
                      <path d="M4.844 12.239V4.79m0 0h7.448m-7.448 0 8.125 8.126" stroke="currentColor" stroke-width="2" stroke-linejoin="bevel"></path>
                    </svg>
                  </span>
                </button>
              </div>
            </li>
          `;
        }
      });

      suggestionsHTML += `
          </ul>
        </div>
      `;

      return suggestionsHTML;
    }

    /**
     * Escapes HTML to prevent XSS.
     * @param {string} text - Text to escape.
     * @returns {string} - Escaped text.
     */
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      if (!this.results) {
        // Fallback: try to find results element
        this.results = document.querySelector('.header__search-content .js-search-results') ||
          document.querySelector('.search-dialog_searchWrapper___kGSn .js-search-results') ||
          document.querySelector('.dialog_content__u_95A .js-search-results');
      }
      if (this.results) {
        this.results.innerHTML = resultsMarkup;
        this.setAttribute('results', true);
        this.open();
        // Show search content area when results are rendered
        const searchContent = document.querySelector('.header__search-content');
        if (searchContent && resultsMarkup.trim()) {
          searchContent.removeAttribute('hidden');
        }
        // Add event listeners for search suggestion buttons
        this.attachSuggestionListeners();
        // Populate and hide/show recent searches block, attach recent list listeners
        this.updateRecentSearchesUI();
        this.attachRecentSearchesListeners();
      }
    }

    /**
     * Returns recent search terms from localStorage.
     * @returns {string[]}
     */
    static getRecentSearches() {
      try {
        const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
        return raw ? JSON.parse(raw) : [];
      } catch (e) {
        return [];
      }
    }

    /**
     * Appends a search term to recent list (dedupe, newest first, max MAX_RECENT_SEARCHES).
     * @param {string} term - Search query.
     */
    static saveRecentSearch(term) {
      if (!term || !term.trim()) return;
      const trimmed = term.trim();
      let list = PredictiveSearch.getRecentSearches();
      list = list.filter((t) => t.toLowerCase() !== trimmed.toLowerCase());
      list.unshift(trimmed);
      list = list.slice(0, MAX_RECENT_SEARCHES);
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(list));
      } catch (e) {}
    }

    /**
     * Fills the recent searches list in the DOM and shows/hides the block.
     */
    updateRecentSearchesUI() {
      if (!this.results) return;
      const block = this.results.querySelector('.js-recent-searches-block');
      const listEl = this.results.querySelector('.js-recent-searches-list');
      if (!block || !listEl) return;
      const items = PredictiveSearch.getRecentSearches();
      if (items.length === 0) {
        block.style.display = 'none';
        listEl.innerHTML = '';
        return;
      }
      block.style.display = 'block';
      const fillArrowSvg = '<span class="icon_iconContainer__lS_O1"><svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 16" aria-hidden="true"><path d="M4.844 12.239V4.79m0 0h7.448m-7.448 0 8.125 8.126" stroke="currentColor" stroke-width="2" stroke-linejoin="bevel"></path></svg></span>';
      const removeTrashSvg = '<span class="icon_iconContainer__lS_O1 remove-item"><svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 16" aria-hidden="true"><path d="m4.906 4.802.8 8.8h6.4l.8-8.8m-8 0h-1.6m1.6 0h1.6m6.4 0h1.6m-1.6 0h-1.6m-4.8 0v-2.4h4.8v2.4m-4.8 0h4.8" stroke="currentColor" stroke-width="2" stroke-linecap="square" stroke-linejoin="bevel"></path></svg></span>';
      const fillSuggestionTemplate = this.getAttribute('data-fill-suggestion-template') || 'Fill in the search field with "__SUGGESTION__"';
      const removeRecentSearchText = this.getAttribute('data-remove-recent-search') || 'Remove this search';
      const escape = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      listEl.innerHTML = items.map((term) => {
        const escaped = escape(term);
        const fillSuggestionText = escape(fillSuggestionTemplate.replace('__SUGGESTION__', term));
        const removeSearchText = escape(removeRecentSearchText);
        return `<li><div class="search-dialog_searchItem__Cni4_ search-dialog_upp__xrTEY"><p>${escaped}</p><button type="button" class="js-search-suggestion focus-inset" data-suggestion="${escaped}" title="${fillSuggestionText}" aria-label="${fillSuggestionText}">${fillArrowSvg}</button><button type="button" class="js-remove-recent-search remove-item focus-inset" title="${removeSearchText}" aria-label="${removeSearchText}" data-recent-term="${escaped}">${removeTrashSvg}</button></div></li>`;
      }).join('');
    }

    /**
     * Attaches click handlers for recent search items (fill input, remove item).
     */
    attachRecentSearchesListeners() {
      if (!this.results) return;
      const listEl = this.results.querySelector('.js-recent-searches-list');
      if (!listEl) return;
      listEl.addEventListener('click', (e) => {
        const fillBtn = e.target.closest('.js-search-suggestion[data-suggestion]');
        const removeBtn = e.target.closest('.js-remove-recent-search');
        if (fillBtn) {
          e.preventDefault();
          const suggestion = fillBtn.getAttribute('data-suggestion');
          if (suggestion) {
            this.input.value = suggestion;
            this.input.setAttribute('value', suggestion);
            this.input.dispatchEvent(new Event('input', { bubbles: true }));
            this.input.focus();
          }
        } else if (removeBtn) {
          e.preventDefault();
          const term = removeBtn.getAttribute('data-recent-term');
          if (term) {
            let list = PredictiveSearch.getRecentSearches().filter((t) => t !== term);
            try {
              localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(list));
            } catch (err) {}
            this.updateRecentSearchesUI();
          }
        }
      });
    }

    /**
     * Attaches event listeners to search suggestion buttons.
     */
    attachSuggestionListeners() {
      const suggestionButtons = this.results.querySelectorAll('.js-search-suggestion');
      suggestionButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          const suggestion = button.getAttribute('data-suggestion');
          if (suggestion) {
            this.input.value = suggestion;
            this.input.setAttribute('value', suggestion);
            // Trigger input event to show search results
            this.input.dispatchEvent(new Event('input', { bubbles: true }));
            this.input.focus();
          }
        });
      });
    }

    /**
     * Opens the results listbox.
     */
    open() {
      if (this.resetBtn) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      if (this.resetBtn) this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.input.setAttribute('value', '');
        this.removeAttribute('results');
        // Trigger input event to update the UI
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      if (this.resetBtn) this.resetBtn.hidden = true;
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');

      // Hide search content area when closing
      const searchContent = document.querySelector('.header__search-content');
      if (searchContent) searchContent.setAttribute('hidden', '');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      if (this.resetBtn) this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
